/**
 * Edit Event Page - REDESIGNED
 * Layout mirrors EventDetailPage but with editable form fields
 * Two-column layout: Details form (left) | Flyer uploader (right)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import ImageUploader from '../components/ImageUploader';
import Navbar from '../components/Navbar';
import '../styles/colors.css';

const GENRE_LIST = [
  'Ambient', 'Alternative', 'Blues', 'Classical', 'Country', 'Cultural',
  'Dance', 'EDM', 'Electronic', 'Emo', 'Experimental', 'Folk', 'Grunge',
  'Hardcore', 'Hip-hop', 'Instrumental', 'Jazz', 'Metal', 'Noise', 'Pop',
  'Psychedelic', 'Punk', 'R&B', 'Rap', 'Reggae-Ska', 'Religious', 'Rock',
  'Shoegaze', 'Singer-Songwriter', 'Spoken Word'
];

const EditEventPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    event_date: '',
    door_time: '',
    music_time: '',
    ticket_price: '',
    flyer_url: '',
    age_restriction: 'all_ages',
    ticket_link: '',
    description: '',
    genre_tags: [],
    city: '',
    state: '',
    address: ''
  });

  const [collaborators, setCollaborators] = useState({ bands: [], venues: [], promoters: [], vendors: [] });
  const [originalCollaborators, setOriginalCollaborators] = useState({ bands: [], venues: [], promoters: [], vendors: [] });
  const [showAddForm, setShowAddForm] = useState({ band: false, venue: false, promoter: false, vendor: false });
  const [searchQueries, setSearchQueries] = useState({ band: '', venue: '', promoter: '', vendor: '' });
  const [searchResults, setSearchResults] = useState({ band: [], venue: [], promoter: [], vendor: [] });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEventAndCollaborators();
  }, [id]);

  useEffect(() => {
    const performSearch = async (role) => {
      const query = searchQueries[role];
      if (!query || query.trim().length < 2) {
        setSearchResults(prev => ({ ...prev, [role]: [] }));
        return;
      }

      try {
        const response = await apiClient.get(`/search/users?q=${encodeURIComponent(query)}&role=${role}`);
        if (response.data.success) {
          setSearchResults(prev => ({ ...prev, [role]: response.data.users }));
        }
      } catch (err) {
        console.error('Search failed:', err);
      }
    };

    const timers = {};
    ['band', 'venue', 'promoter', 'vendor'].forEach(role => {
      timers[role] = setTimeout(() => performSearch(role), 300);
    });

    return () => Object.values(timers).forEach(timer => clearTimeout(timer));
  }, [searchQueries]);

  const fetchEventAndCollaborators = async () => {
    try {
      const eventResponse = await apiClient.get(`/events/${id}`);
      if (eventResponse.data.success) {
        const event = eventResponse.data.event;
        // Convert ISO date to yyyy-MM-dd format
        const dateOnly = event.event_date ? event.event_date.split('T')[0] : '';
        setFormData({
          title: event.title || '',
          event_date: dateOnly,
          door_time: event.door_time || '',
          music_time: event.music_time || '',
          ticket_price: event.ticket_price || '',
          flyer_url: event.flyer_url || '',
          age_restriction: event.age_restriction || 'all_ages',
          ticket_link: event.ticket_link || '',
          description: event.description || '',
          genre_tags: event.genre_tags || [],
          city: event.city || '',
          state: event.state || '',
          address: event.address || ''
        });
      }

      const collabResponse = await apiClient.get(`/events/${id}/collaborators`);
      if (collabResponse.data.success) {
        const collabs = collabResponse.data.collaborators;
        const collabState = {
          bands: collabs.filter(c => c.collaborator_role === 'band'),
          venues: collabs.filter(c => c.collaborator_role === 'venue'),
          promoters: collabs.filter(c => c.collaborator_role === 'promoter'),
          vendors: collabs.filter(c => c.collaborator_role === 'vendor' || c.collaborator_role === 'photographer')
        };
        setCollaborators(collabState);
        setOriginalCollaborators(JSON.parse(JSON.stringify(collabState))); // Deep copy
      }
    } catch (err) {
      setError('Failed to load event');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genre_tags: prev.genre_tags.includes(genre)
        ? prev.genre_tags.filter(g => g !== genre)
        : [...prev.genre_tags, genre]
    }));
  };

  const handleAddCollaborator = (role, result) => {
    const newCollab = {
      collaborator_id: result.id,
      collaborator_name: result.display_name,
      name: result.display_name, // For create page compatibility
      id: result.id,
      collaborator_role: role,
      isNew: true // Mark as new so we know to POST it
    };
    setCollaborators(prev => ({
      ...prev,
      [`${role}s`]: [...prev[`${role}s`], newCollab]
    }));
    
    // If adding a venue with address info, inherit it
    if (role === 'venue' && result.id) {
      if (result.city && !formData.city) setFormData(prev => ({ ...prev, city: result.city }));
      if (result.state && !formData.state) setFormData(prev => ({ ...prev, state: result.state }));
      if (result.address && !formData.address) setFormData(prev => ({ ...prev, address: result.address }));
    }
    
    setSearchQueries(prev => ({ ...prev, [role]: '' }));
    setSearchResults(prev => ({ ...prev, [role]: [] }));
    setShowAddForm(prev => ({ ...prev, [role]: false }));
  };

  const handleAddPlaintextCollaborator = (role) => {
    const searchText = searchQueries[role].trim();
    if (!searchText) return;
    
    const newCollab = {
      collaborator_id: null,  // null = plaintext, not linked
      collaborator_name: searchText,
      name: searchText,
      id: null,
      collaborator_role: role,
      isNew: true
    };
    
    setCollaborators(prev => ({
      ...prev,
      [`${role}s`]: [...prev[`${role}s`], newCollab]
    }));
    
    setSearchQueries(prev => ({ ...prev, [role]: '' }));
    setSearchResults(prev => ({ ...prev, [role]: [] }));
    setShowAddForm(prev => ({ ...prev, [role]: false }));
  };

  const handleRemoveCollaborator = (role, index) => {
    setCollaborators(prev => ({
      ...prev,
      [`${role}s`]: prev[`${role}s`].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // Check if a LINKED venue exists (not plaintext) - only linked venues have location to inherit
    const hasLinkedVenue = collaborators.venues && collaborators.venues.some(v => v.collaborator_id !== null);
    
    if (!hasLinkedVenue && (!formData.city || !formData.state)) {
      setError('City and State are required when no linked venue is present');
      setSaving(false);
      return;
    }

    try {
      const updateData = { ...formData };
      
      // If LINKED venue exists, remove location fields (backend will inherit from venue account)
      // Plaintext venues have no location, so user must provide city/state
      if (hasLinkedVenue) {
        delete updateData.city;
        delete updateData.state;
        delete updateData.address;
      }
      
      console.log('Submitting update:', updateData);
      const response = await apiClient.put(`/events/${id}`, updateData);
      console.log('Update response:', response.data);

      if (response.data.success) {
        // Handle collaborator changes
        // 1. Add new collaborators (marked with isNew)
        const allCurrentCollabs = [
          ...collaborators.bands,
          ...collaborators.venues,
          ...collaborators.promoters,
          ...collaborators.vendors
        ];

        for (const collab of allCurrentCollabs) {
          if (collab.isNew) {
            try {
              await apiClient.post(`/events/${id}/collaborators`, {
                collaborator_name: collab.collaborator_name || collab.name,
                collaborator_role: collab.collaborator_role,
                collaborator_id: collab.collaborator_id || collab.id || null
              });
              console.log('Added collaborator:', collab.collaborator_name);
            } catch (err) {
              console.error('Failed to add collaborator:', err);
            }
          }
        }

        // 2. Delete removed collaborators
        const allOriginalCollabs = [
          ...originalCollaborators.bands,
          ...originalCollaborators.venues,
          ...originalCollaborators.promoters,
          ...originalCollaborators.vendors
        ];

        for (const originalCollab of allOriginalCollabs) {
          // Check if this collaborator still exists in current state
          const stillExists = allCurrentCollabs.some(c => 
            c.id === originalCollab.id || c.collaborator_id === originalCollab.collaborator_id
          );
          
          if (!stillExists && originalCollab.id) {
            try {
              await apiClient.delete(`/events/${id}/collaborators/${originalCollab.id}`);
              console.log('Removed collaborator:', originalCollab.collaborator_name);
            } catch (err) {
              console.error('Failed to remove collaborator:', err);
            }
          }
        }

        alert('Event updated successfully!');
        navigate(`/events/${id}`);
      }
    } catch (err) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || err.message || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)' }}>
          Loading event...
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .edit-event-input {
            font-size: 16px !important;
          }

          .edit-event-textarea {
            font-size: 16px !important;
          }

          .edit-event-title-input {
            font-size: 24px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .edit-event-two-column {
            display: flex !important;
            flex-direction: column !important;
          }

          .edit-event-flyer-section {
            order: -1 !important;
            margin-bottom: 24px !important;
          }

          .edit-event-header {
            flex-direction: row !important;
            justify-content: space-between !important;
            align-items: center !important;
            gap: 16px !important;
          }

          .edit-event-header h1 {
            font-size: 24px !important;
            text-align: center !important;
            flex: 1 !important;
          }

          .edit-event-header-spacer {
            width: 80px !important;
          }
        }
      `}</style>
      <Navbar />
      <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>

          <div className="edit-event-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <button onClick={() => navigate(`/events/${id}`)} className="btn btn-secondary">Cancel</button>
            <h1 style={{ color: 'var(--text-light)', fontSize: '32px', fontWeight: '700', margin: 0 }}>Edit Event</h1>
            <div className="edit-event-header-spacer" style={{ width: '100px' }}></div>
          </div>

          {error && (
            <div style={{ background: 'var(--accent-danger)', color: 'var(--text-light)', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            
            {/* Title Input */}
            <div style={{ marginBottom: '24px' }}>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Event Title"
                className="edit-event-title-input"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '32px',
                  fontWeight: '700',
                  background: 'var(--bg-card)',
                  color: 'var(--text-dark)',
                  border: '2px solid var(--border-light)',
                  borderRadius: '8px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
              />
            </div>

            {/* Age Restriction Selector */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-light)', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase' }}>
                Age Restriction *
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                {['all_ages', '18_plus', '21_plus'].map(age => (
                  <button
                    key={age}
                    type="button"
                    onClick={() => setFormData({ ...formData, age_restriction: age })}
                    style={{
                      padding: '12px 24px',
                      background: formData.age_restriction === age ? 'var(--accent-primary)' : 'var(--smoke-gray)',
                      color: formData.age_restriction === age ? 'var(--text-dark)' : 'var(--text-muted)',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {age === 'all_ages' ? 'All Ages' : age === '18_plus' ? '18+' : '21+'}
                  </button>
                ))}
              </div>
            </div>

            {/* Two-Column Layout: Details Form | Flyer Upload */}
            <div className="edit-event-two-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px' }}>
              
              {/* LEFT: Details Form */}
              <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    Event Date *
                  </label>
                  <input
                    type="date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleChange}
                    required
                    className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                      Doors *
                    </label>
                    <input
                      type="time"
                      name="door_time"
                      value={formData.door_time}
                      onChange={handleChange}
                      required
                      className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                      Music *
                    </label>
                    <input
                      type="time"
                      name="music_time"
                      value={formData.music_time}
                      onChange={handleChange}
                      required
                      className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    Entry Cost *
                  </label>
                  <input
                    type="number"
                    name="ticket_price"
                    value={formData.ticket_price}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    Ticket Link
                  </label>
                  <input
                    type="url"
                    name="ticket_link"
                    value={formData.ticket_link}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    City {(!collaborators.venues || !collaborators.venues.some(v => v.collaborator_id !== null)) && '*'}
                    {collaborators.venues && collaborators.venues.some(v => v.collaborator_id !== null) && ' (Optional - will inherit from venue)'}
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required={!collaborators.venues || !collaborators.venues.some(v => v.collaborator_id !== null)}
                    className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    State {(!collaborators.venues || !collaborators.venues.some(v => v.collaborator_id !== null)) && '*'}
                    {collaborators.venues && collaborators.venues.some(v => v.collaborator_id !== null) && ' (Optional - will inherit from venue)'}
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required={!collaborators.venues || !collaborators.venues.some(v => v.collaborator_id !== null)}
                    className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>

                <div style={{ marginBottom: '0' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Street address"
                    className="edit-event-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>
              </div>

              {/* RIGHT: Flyer Upload */}
              <div className="edit-event-flyer-section" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <label style={{ display: 'block', marginBottom: '12px', color: 'var(--text-light)', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase' }}>
                    Event Flyer
                  </label>
                  <ImageUploader
                    currentImageUrl={formData.flyer_url}
                    onImageUploaded={(url) => setFormData({ ...formData, flyer_url: url })}
                    folder="events"
                    previewSize={300}
                  />
                </div>
              </div>
            </div>

            {/* Collaborators - Functional with better styling */}
            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '700', color: 'var(--text-dark)' }}>Collaborators</h3>
              
              {['band', 'venue', 'promoter', 'vendor'].map(role => (
                <div key={role} style={{ marginBottom: '28px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <strong style={{ textTransform: 'capitalize', fontSize: '15px', color: 'var(--text-dark)', fontWeight: '700' }}>
                      {role === 'band' ? '🎸' : role === 'venue' ? '🏛️' : role === 'promoter' ? '📣' : '📸'} {role}s
                    </strong>
                    <button
                      type="button"
                      onClick={() => setShowAddForm(prev => ({ ...prev, [role]: !prev[role] }))}
                      style={{ 
                        padding: '8px 16px', 
                        fontSize: '13px', 
                        background: 'var(--accent-primary)', 
                        color: 'var(--text-dark)', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        fontWeight: '700',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      + Add {role}
                    </button>
                  </div>

                  {/* Selected Collaborators */}
                  {collaborators[`${role}s`] && collaborators[`${role}s`].length > 0 && (
                    <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {collaborators[`${role}s`].map((collab, i) => (
                        <div 
                          key={`${role}-${collab.collaborator_id || collab.id || i}`} 
                          style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: 'var(--accent-primary)',
                            color: 'var(--text-dark)',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          <span>{collab.collaborator_name || collab.name}</span>
                          <button 
                            type="button" 
                            onClick={() => handleRemoveCollaborator(role, i)} 
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: 'var(--accent-danger)', 
                              cursor: 'pointer', 
                              fontSize: '16px', 
                              fontWeight: '700',
                              padding: '0',
                              lineHeight: '1'
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Search Form */}
                  {showAddForm[role] && (
                    <div style={{ padding: '16px', background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: '8px' }}>
                      <input
                        type="text"
                        placeholder={`Search for ${role}...`}
                        value={searchQueries[role]}
                        onChange={(e) => setSearchQueries(prev => ({ ...prev, [role]: e.target.value }))}
                        style={{ 
                          width: '100%', 
                          padding: '12px', 
                          border: '1px solid var(--border-light)', 
                          borderRadius: '6px', 
                          marginBottom: '8px',
                          fontSize: '14px'
                        }}
                      />
                      {searchResults[role].length > 0 && (
                        <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border-light)', borderRadius: '6px', background: 'white' }}>
                          {searchResults[role].map(result => (
                            <div
                              key={result.id}
                              onClick={() => handleAddCollaborator(role, result)}
                              style={{ 
                                padding: '12px', 
                                cursor: 'pointer', 
                                borderBottom: '1px solid var(--border-light)', 
                                color: 'var(--text-dark)',
                                fontSize: '14px',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'var(--smoke-gray)';
                                e.target.style.color = 'var(--accent-primary)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'white';
                                e.target.style.color = 'var(--text-dark)';
                              }}
                            >
                              <strong>{result.display_name}</strong>
                              <span style={{ marginLeft: '8px', fontSize: '13px', opacity: 0.7 }}>
                                ({result.city}, {result.state})
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {searchQueries[role].length >= 2 && searchResults[role].length === 0 && (
                        <div style={{ padding: '12px' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '14px', fontStyle: 'italic', marginBottom: '12px' }}>
                            No results found
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddPlaintextCollaborator(role)}
                            className="btn btn-primary"
                            style={{ padding: '8px 16px', fontSize: '14px', width: '100%' }}
                          >
                            Add "{searchQueries[role]}" as plaintext
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Genre Tags - Only show if no bands linked */}
            {(!collaborators.bands || collaborators.bands.length === 0) && (
              <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
                <label style={{ display: 'block', marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                  Genre Tags (No limit when no bands on bill)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  {GENRE_LIST.map((genre, index) => (
                    <label key={`genre-${genre}-${index}`} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.genre_tags.includes(genre)}
                        onChange={() => handleGenreToggle(genre)}
                        style={{ marginRight: '8px', width: '18px', height: '18px' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-dark)' }}>{genre}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="6"
                className="edit-event-textarea"
                style={{
                  width: '100%',
                  padding: '16px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  lineHeight: '1.6'
                }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
              style={{ width: '100%', padding: '18px', fontSize: '18px', fontWeight: '700' }}
            >
              {saving ? 'Saving...' : 'Update Event'}
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default EditEventPage;
